// (Copyright) Confluent, Inc.

import * as React from 'react';
import styled, { css } from 'styled-components';

import { type Color } from '../../constants/colors';
import { highlightedShadow } from '../../constants/shadows';
import { cssColor } from '../../utils/color';
import { type Padding, paddingProp } from '../../utils/padding';
import { type WidthProps, widthProps } from '../../utils/width';
import { type HeightProps, heightProps } from '../../utils/height';
import { withDisableStyleOverrides } from '../../utils/disableOverride';
import LegacyTooltip, { type LegacyTooltipProps } from '../LegacyTooltip';

export type CardProps = {
  ['data-testid']?: string;
  ariaSelected?: boolean;
  backgroundColor?: Color;
  cardRef?: any;
  children: React.ReactNode;
  disabled?: boolean;
  highlight?: boolean;
  highlightColor?: Color;
  highlightPosition?: 'top' | 'bottom' | 'left' | 'right';
  isStatic?: boolean;
  onClick?: () => void | null | undefined;
  onMouseEnter?: () => void | null | undefined;
  onMouseLeave?: () => void | null | undefined;
  overflowIsHidden?: boolean;
  padding?: Padding;
  title?: string;
  tooltipProps?: Partial<LegacyTooltipProps>;
} & WidthProps &
  HeightProps;

export const staticCardStyles = css`
  border-radius: 8px;
  box-shadow: var(--SHADOW_STATIC);
`;

const DEFAULT_PADDING = 24;

const CardContainer = styled.div<any>`
  /* note: added for the topic empty state card in topicsTable,
   * which has a background in the lower half (not the entire component),
   * and was overflowing */
  ${({ overflowIsHidden }) => (overflowIsHidden ? ' overflow: hidden;' : '')}
  background-color: ${({ backgroundColor }) =>
    // eslint-disable-next-line rulesdir/no-css-var-colors
    backgroundColor ? cssColor(backgroundColor) : 'var(--WHITE)'};
  ${staticCardStyles}
  ${paddingProp(DEFAULT_PADDING)}
  ${widthProps()}
  ${heightProps()}
  box-shadow: ${({ highlight, highlightColor, disabled, isStatic, backgroundColor }) => {
    if (backgroundColor) {
      return 'none';
    }

    if (highlight) {
      return `${highlightedShadow(highlightColor)}`;
    }

    if (disabled || isStatic) {
      return 'var(--SHADOW_STATIC)';
    }

    return 'var(--SHADOW_DEFAULT)';
  }};

  transition: box-shadow ease-out 200ms;
  position: relative;

  cursor: ${({ onClick, highlight }) => (onClick && !highlight ? `pointer` : 'inherit')};

  &:hover {
    box-shadow: ${({ highlight, disabled, onClick, isStatic }) =>
      onClick && !highlight && !disabled && !isStatic ? `var(--SHADOW_HOVER)` : ''};
  }

  ${({ onClick, highlight }) =>
    onClick && !highlight
      ? `
    &:active {
      box-shadow: var(--SHADOW_PRESSED);
    }
`
      : ''}

  ${({ disabled }) =>
    disabled &&
    `
    > * {
      opacity: var(--OPACITY_60);
    }
  `}
`;

const HighlightBarContainer = styled.div<any>`
  position: absolute;
  overflow: hidden;
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};

  ${({ position }) =>
    css`
      /* TODO: This should be a function */
      ${position === 'top'
        ? css`
            top: -1px;
            left: -1px;
            right: -1px;
            height: 8px;
            border-top-left-radius: 8px;
            border-top-right-radius: 8px;
          `
        : ''}
      ${position === 'right'
        ? css`
            top: -1px;
            bottom: -1px;
            right: -1px;
            width: 8px;
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
          `
        : ''}
      ${position === 'bottom'
        ? css`
            left: -1px;
            right: -1px;
            bottom: -1px;
            height: 8px;
            border-bottom-left-radius: 8px;
            border-bottom-right-radius: 8px;
          `
        : ''}
      ${position === 'left'
        ? css`
            top: -1px;
            bottom: -1px;
            left: -1px;
            width: 8px;
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
          `
        : ''}
    `}
`;

const HighlightBarContents = styled.div<any>`
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: transform ease-out 150ms, opacity ease-out 150ms;

  ${({ position }) => {
    switch (position) {
      case 'top':
        return css`
          transform-origin: 0% 0%;
          transform: scaleY(0);
        `;
      case 'right':
        return css`
          transform-origin: 100% 100%;
          transform: scaleX(0);
        `;
      case 'bottom':
        return css`
          transform-origin: 100% 100%;
          transform: scaleY(0);
        `;
      case 'left':
        return css`
          transform-origin: 0% 0%;
          transform: scaleX(0);
        `;
      default:
        return '';
    }
  }}

  ${({ color, isVisible }) => {
    if (!isVisible) {
      return '';
    }

    return css`
      background-color: ${cssColor(color)};
      opacity: 1;
      transform: scale(1);
    `;
  }}
`;

const HighlightBar = (props: {
  color?: Color;
  isVisible: boolean;
  position?: 'top' | 'bottom' | 'left' | 'right';
}) => {
  return (
    <HighlightBarContainer
      aria-hidden="true"
      color={props.color}
      isVisible={props.isVisible}
      position={props.position}
    >
      <HighlightBarContents
        color={props.color}
        isVisible={props.isVisible}
        position={props.position}
      />
    </HighlightBarContainer>
  );
};

export const CardWrapper = (props: CardProps) => {
  const { ariaSelected, cardRef, children, highlightPosition, ...propsToPass } = props;

  return (
    <CardContainer aria-selected={ariaSelected} {...propsToPass} ref={cardRef}>
      <HighlightBar
        color={props.highlightColor}
        isVisible={Boolean(props.highlight)}
        position={highlightPosition}
      />
      {children}
    </CardContainer>
  );
};

const Card = ({ children, ...rest }: CardProps) => {
  const props = rest;

  if (props.disabled && props.onClick) {
    delete props.onClick;
  }

  const content = <CardWrapper {...props}>{children}</CardWrapper>;

  if (props.tooltipProps) {
    return <LegacyTooltip {...props.tooltipProps}>{content}</LegacyTooltip>;
  }

  return content;
};

Card.defaultProps = {
  ariaSelected: null,
  disabled: false,
  highlight: false,
  highlightColor: 'PRIMARY',
  highlightPosition: 'left',
  isStatic: false,
  padding: DEFAULT_PADDING,
};

// @ts-expect-error - TS2345 - Argument of type '{ ({ children, ...rest }: CardProps): JSX.Element; defaultProps: { ariaSelected: any; disabled: boolean; highlight: boolean; highlightColor: string; highlightPosition: string; isStatic: boolean; padding: number; }; }' is not assignable to parameter of type 'string | ComponentType<CardProps>'.
export default withDisableStyleOverrides<CardProps>(Card);
